import { type TableOptions_All } from '@tanstack/react-table';

import type {
  DataTableColumnSort,
  DataTableSortingProps,
} from './sorting-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

const defaultSortBy: DataTableColumnSort[] = [];

/**
 * Configuration hook for the DataTable sorting feature.
 * @internal
 */
export function useSorting<TData extends DataTableRowData>(
  props: DataTableSortingProps,
  options: TableOptions_All<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const configSortable = useDataTableConfigElement('sortable');

  const [sorting, setSorting] = useControllableState<DataTableColumnSort[]>({
    value: props.sortBy,
    defaultValue: props.defaultSortBy ?? defaultSortBy,
    onChange: props.onSortByChange,
  });

  const { sortable: sortableProp } = props;
  let sortable = false;

  if (sortableProp !== undefined) {
    if (typeof sortableProp === 'boolean') {
      sortable = sortableProp;
      // eslint-disable-next-line react-hooks/immutability
      options.manualSorting = false;
    } else {
      sortable = true;
      // To do: should reconsider is there a better and simpler way (simpler API exposed) that we can check wheater the consumer wants to keep default sorting
      // or they want to implement custom data pre sorting
      if (sortableProp.manualSort === true) {
        // eslint-disable-next-line react-hooks/immutability
        options.manualSorting = true;
      } else {
        // eslint-disable-next-line react-hooks/immutability
        options.manualSorting = false;
      }
    }
  } else if (configSortable !== undefined) {
    if (typeof configSortable === 'boolean') {
      sortable = configSortable;
    } else {
      sortable = true;

      if (configSortable.manualSort === true) {
        // eslint-disable-next-line react-hooks/immutability
        options.manualSorting = true;
      } else {
        // eslint-disable-next-line react-hooks/immutability
        options.manualSorting = false;
      }
    }
  }

  // eslint-disable-next-line react-hooks/immutability
  options.enableSorting = sortable;
  // eslint-disable-next-line react-hooks/immutability
  options.state = {
    ...options.state,
    sorting,
  };
  // eslint-disable-next-line react-hooks/immutability
  options.onSortingChange = setSorting;
}
